import React, { useState, useRef } from 'react';
import { Table, Button, Space, Typography, Checkbox, message } from 'antd';
import { FileExcelOutlined, ClearOutlined } from '@ant-design/icons';
import type { TableProps } from 'antd';
import { formatCurrency } from '../withdrawal-check/utils';

const { Text } = Typography;

interface ExportableTableProps<T> extends Omit<TableProps<T>, 'columns'> {
  columns: any[];
  onExport: (selectedRecords: T[]) => Promise<void>;
  exportTitle: string;
  isAdmin?: boolean;
}

function ExportableTable<T extends { id: number }>({
  columns,
  dataSource,
  onExport,
  exportTitle,
  isAdmin = false,
  ...tableProps
}: ExportableTableProps<T>) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const tableRef = useRef<any>(null);
  const [currentFilteredData, setCurrentFilteredData] = useState<T[]>([]);

  React.useEffect(() => {
    if (dataSource) {
      setCurrentFilteredData([...dataSource]);
    }
  }, [dataSource]);
  // Add selection column
  const selectionColumn = {
    title: 'Chọn',
    key: 'select',
    width: 60,
    render: (_: any, record: T) => (
      <Checkbox
        checked={selectedRowKeys.includes(record.id)}
        onChange={(e) => {
          if (e.target.checked) {
            setSelectedRowKeys([...selectedRowKeys, record.id]);
          } else {
            setSelectedRowKeys(
              selectedRowKeys.filter((key) => key !== record.id)
            );
          }
        }}
      />
    ),
  };

  // Handle table change
  const handleTableChange: TableProps<T>['onChange'] = (
    pagination,
    filters,
    sorter,
    extra
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    if (extra.currentDataSource) {
      setCurrentFilteredData([...extra.currentDataSource]);
    }
    if (tableProps.onChange)
      tableProps.onChange(pagination, filters, sorter, extra);
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const currentIds = currentFilteredData.map((record) => record.id);
      const newSelections = Array.from(
        new Set([...selectedRowKeys, ...currentIds])
      );
      setSelectedRowKeys(newSelections);
    } else {
      const currentIds = currentFilteredData.map((record) => record.id);
      const newSelections = selectedRowKeys.filter(
        (id) => !currentIds.includes(id)
      );
      setSelectedRowKeys(newSelections);
    }
  };

  // Handle clear filters
  const handleClearAllFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setCurrentFilteredData([...(dataSource || [])]);
    message.success('Đã xóa tất cả bộ lọc');
  };

  // Handle export
  const handleExport = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một bản ghi để xuất Excel');
      return;
    }

    try {
      setLoading(true);
      const selectedRecords = (dataSource || []).filter((record) =>
        selectedRowKeys.includes(record.id)
      );

      await onExport(selectedRecords);
      message.success(
        `Đã xuất Excel thành công cho ${selectedRecords.length} bản ghi`
      );
    } catch (error) {
      console.error('Error exporting Excel:', error);
      message.error('Có lỗi xảy ra khi xuất Excel');
    } finally {
      setLoading(false);
    }
  };

  // Add export button to columns
  const exportColumn = {
    title: 'Xuất Excel',
    key: 'export',
    width: 100,
    fixed: 'right',
    render: () => (
      <Button
        type="primary"
        icon={<FileExcelOutlined />}
        onClick={handleExport}
        loading={loading}
        disabled={selectedRowKeys.length === 0}
        size="small"
      >
        Xuất ({selectedRowKeys.length})
      </Button>
    ),
  };

  // Map filteredValue/sortOrder vào từng column
  const mapColumns = (cols: any[]) =>
    cols.map((col) => {
      const newCol = { ...col };
      if (col.key && filteredInfo) {
        newCol.filteredValue = filteredInfo[col.key] || null;
      }
      if (col.key && sortedInfo && sortedInfo.columnKey === col.key) {
        newCol.sortOrder = sortedInfo.order;
      }
      if (col.children) {
        newCol.children = mapColumns(col.children);
      }
      return newCol;
    });

  const allColumns = [selectionColumn, ...mapColumns(columns), exportColumn];

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <Space>
          <Checkbox
            checked={
              currentFilteredData.length > 0 &&
              currentFilteredData.every((record) =>
                selectedRowKeys.includes(record.id)
              )
            }
            indeterminate={
              selectedRowKeys.length > 0 &&
              currentFilteredData.some((record) =>
                selectedRowKeys.includes(record.id)
              ) &&
              !currentFilteredData.every((record) =>
                selectedRowKeys.includes(record.id)
              )
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
          >
            Chọn hiển thị
          </Checkbox>
          <Text type="secondary">
            Đã chọn: {selectedRowKeys.length} bản ghi | Hiển thị:{' '}
            {
              currentFilteredData.filter((record) =>
                selectedRowKeys.includes(record.id)
              ).length
            }
            /{currentFilteredData.length}
          </Text>
        </Space>
        <Space>
          {Object.keys(filteredInfo).length > 0 && (
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearAllFilters}
              type="default"
              size="small"
              className="text-red-500 hover:text-red-700 hover:border-red-300 border-red-200"
            >
              Xóa bộ lọc
            </Button>
          )}
        </Space>
      </div>

      {/* Table */}
      <Table
        ref={tableRef}
        columns={allColumns}
        dataSource={dataSource}
        onChange={handleTableChange}
        rowClassName={(record, index) =>
          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
        }
        className="rounded-lg border border-gray-200 shadow-sm"
        size="middle"
        {...tableProps}
      />
    </div>
  );
}

export default ExportableTable;
